import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, Users, Target, TrendingUp, Plus, ArrowRight, Workflow, UserSearch } from 'lucide-react';
import api from '../../lib/api';
import { jobUrlSegment, kolUrlSegment } from '../../lib/slug';
import { Job, KOLProfile, Organization } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import OnboardingChecklist from '../../components/OnboardingChecklist';
import { StatNumber, StatsGridSkeleton } from '../../components/ui/StatsLoading';
import { AIWorkflowPanel } from '../../components/AIWorkflowPanel';
import { AIKOLDiscoveryPanel } from '../../components/AIKOLDiscoveryPanel';
import AICopilotStrip from '../../components/AICopilotStrip';
import { resolveMediaUrl } from '../../lib/mediaUrl';

function statusPill(status: string) {
  const s = status.toUpperCase();
  if (['ACTIVE', 'ACCEPTED', 'APPROVED', 'COMPLETED'].includes(s)) {
    return s === 'COMPLETED' ? 'status-pill status-pill-muted' : 'status-pill status-pill-pos';
  }
  if (['OPEN', 'VIEWED'].includes(s)) return 'status-pill status-pill-info';
  if (['PAUSED', 'PENDING', 'DRAFT'].includes(s)) return 'status-pill status-pill-warn';
  if (['DECLINED', 'REJECTED', 'CANCELLED'].includes(s)) return 'status-pill status-pill-danger';
  return 'status-pill status-pill-muted';
}

export default function AgencyDashboard() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const organizationId = user?.organizationId;
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [talentScoutOpen, setTalentScoutOpen] = useState(false);

  const dashQuery = useQuery({
    queryKey: ['agency', 'dashboard', organizationId],
    queryFn: async () => {
      const [jobsRes, kolsRes, orgsRes, membersRes] = await Promise.all([
        api.get('/jobs'),
        api.get('/kols'),
        api.get('/organizations'),
        organizationId
          ? api.get(`/organizations/${organizationId}/members`)
          : Promise.resolve({ data: [] }),
      ]);
      const organizations = orgsRes.data as Organization[];
      return {
        jobs: jobsRes.data as Job[],
        kols: kolsRes.data as KOLProfile[],
        brandOrgs: organizations.filter((org) => org.type === 'BRAND'),
        teamMembers: membersRes.data as { id: string }[],
      };
    },
    staleTime: 2 * 60 * 1000,
  });

  const jobs = dashQuery.data?.jobs ?? [];
  const kols = dashQuery.data?.kols ?? [];
  const brandOrgs = dashQuery.data?.brandOrgs ?? [];
  const teamMembers = dashQuery.data?.teamMembers ?? [];
  const refetching = dashQuery.isFetching && !dashQuery.isPending;

  const activeJobs = useMemo(
    () => jobs.filter((j) => ['OPEN', 'ACTIVE'].includes(j.status)),
    [jobs]
  );
  const totalInvitations = jobs.reduce((sum, j) => sum + (j.broadcastedCount || 0), 0);
  const totalAccepted = jobs.reduce((sum, j) => sum + (j.acceptedCount || 0), 0);
  const totalShortlisted = jobs.reduce((sum, j) => sum + (j.shortlistedCount || 0), 0);
  const totalApproved = jobs.reduce((sum, j) => sum + (j.approvedTalentCount || 0), 0);

  const funnel = [
    { label: 'Broadcasted', value: totalInvitations, color: 'bg-[rgb(var(--info))]', pct: 100 },
    {
      label: 'Accepted',
      value: totalAccepted,
      color: 'bg-accent2',
      pct: totalInvitations ? Math.round((totalAccepted / totalInvitations) * 100) : 0,
    },
    {
      label: 'Shortlisted',
      value: totalShortlisted,
      color: 'bg-[rgb(var(--warn))]',
      pct: totalInvitations ? Math.round((totalShortlisted / totalInvitations) * 100) : 0,
    },
    {
      label: 'Approved',
      value: totalApproved,
      color: 'bg-[rgb(var(--pos))]',
      pct: totalInvitations ? Math.round((totalApproved / totalInvitations) * 100) : 0,
    },
  ];

  const onboardingItems = [
    {
      id: 'agency-brands',
      title: 'Add your first brand client',
      hint: 'Create and manage brand records under your agency',
      done: brandOrgs.length > 0,
      to: '/agency/brands',
    },
    {
      id: 'agency-kols',
      title: 'Add your first KOL profile',
      hint: 'Build your KOL database to start matching campaigns',
      done: kols.length > 0,
      to: '/agency/kols',
    },
    {
      id: 'agency-jobs',
      title: 'Create your first campaign',
      hint: 'Set objective, budget and deliverables',
      done: jobs.length > 0,
      to: '/agency/campaigns/new',
    },
    {
      id: 'agency-team',
      title: 'Invite your team members',
      hint: 'Assign managers/staff to active campaigns',
      done: teamMembers.length > 1,
      to: '/agency/team',
    },
  ];

  const stats = [
    { title: 'Active Campaigns', value: activeJobs.length, icon: Briefcase, link: '/agency/campaigns' },
    { title: 'Total KOLs', value: kols.length, icon: Users, link: '/agency/kols' },
    { title: 'Invitations Sent', value: totalInvitations, icon: Target, link: '/agency/campaigns' },
    {
      title: 'Acceptance Rate',
      value: totalInvitations > 0 ? `${Math.round((totalAccepted / totalInvitations) * 100)}%` : '0%',
      icon: TrendingUp,
      link: '/agency/campaigns',
    },
  ];

  const firstName = (user?.fullName || 'there').split(' ')[0];

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start justify-between gap-3 sm:items-end">
        <div>
          <h1 className="page-title text-[1.375rem] md:text-[1.85rem]">Good day, {firstName}</h1>
          <p className="text-glass-text-2 mt-1 max-w-[15rem] text-xs md:max-w-none md:text-sm">
            Your agency command center — campaigns, talent, and AI copilots.
          </p>
        </div>
        <button
          type="button"
          className="btn-accent inline-flex h-11 w-11 shrink-0 items-center justify-center p-0 text-sm font-semibold md:h-auto md:w-auto md:gap-2 md:px-4 md:py-2.5"
          onClick={() => navigate('/agency/campaigns/new')}
          aria-label="New campaign"
          title="New campaign"
        >
          <Plus className="h-5 w-5 md:h-4 md:w-4" />
          <span className="hidden md:inline">New Campaign</span>
        </button>
      </div>

      <div className="flex flex-col gap-4">
        <div className="order-1 md:order-2">
          {dashQuery.isPending ? (
            <StatsGridSkeleton
              count={4}
              className="!grid-cols-2 !gap-2 [&>div]:!p-3 md:!gap-6 md:[&>div]:!p-6 lg:!grid-cols-4"
            />
          ) : (
            <div className="grid grid-cols-2 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-4">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Link
                    key={stat.title}
                    to={stat.link}
                    className="glass-card min-h-[62px] px-3 py-2.5 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150 md:min-h-0 md:p-5"
                  >
                    <div className="flex h-full items-center justify-between gap-2 md:items-start md:gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[10px] uppercase tracking-[0.05em] font-medium text-glass-text-3 md:text-[0.7rem]">
                          {stat.title}
                        </p>
                        <div className="stat-display mt-0.5 flex min-h-[1.65rem] items-center text-[1.375rem] text-glass-text md:mt-2 md:min-h-[2.25rem] md:text-[1.85rem]">
                          <StatNumber loading={refetching}>{stat.value}</StatNumber>
                        </div>
                      </div>
                      <div
                        className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[10px] text-white md:h-10 md:w-10 md:rounded-[13px]"
                        style={{
                          background:
                            'linear-gradient(135deg, rgb(var(--accent-rgb) / 0.9), rgb(var(--accent2-rgb) / 0.7))',
                        }}
                      >
                        <Icon className="h-[15px] w-[15px] md:h-5 md:w-5" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="order-2 space-y-3 md:order-1 md:hidden">
          <AICopilotStrip
            title="Workflow Orchestration"
            description="Stalled-item detection and SLA alerts"
            icon={<Workflow className="h-5 w-5" />}
            statusChips={[
              { label: 'SLA alerts', tone: 'warn' },
              { label: 'Stalled items', tone: 'muted' },
            ]}
            ctaLabel="View report"
            reportOpen={workflowOpen}
            onToggle={() => setWorkflowOpen((open) => !open)}
          >
            <AIWorkflowPanel mode="agency" title="Workflow Orchestration" className="border-0 bg-transparent shadow-none" />
          </AICopilotStrip>
          <AICopilotStrip
            title="Talent Scout"
            description="Talent scans, profile vetting, outreach drafts"
            icon={<UserSearch className="h-5 w-5" />}
            statusChips={[{ label: 'Talent scans', tone: 'info' }]}
            ctaLabel="View report"
            reportOpen={talentScoutOpen}
            onToggle={() => setTalentScoutOpen((open) => !open)}
          >
            <AIKOLDiscoveryPanel mode="agency" title="Talent Scout" className="border-0 bg-transparent shadow-none" />
          </AICopilotStrip>
        </div>

        <div className="order-2 hidden grid-cols-1 gap-4 md:order-1 md:grid xl:grid-cols-2">
          <AIWorkflowPanel mode="agency" title="Workflow Orchestration" />
          <AIKOLDiscoveryPanel mode="agency" title="Talent Scout" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass-card p-5 lg:col-span-1">
          <h2 className="text-[0.95rem] font-semibold text-glass-text mb-4">Talent Pipeline</h2>
          <div className="space-y-3">
            {funnel.map((row) => (
              <div key={row.label}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-glass-text-2 font-medium">{row.label}</span>
                  <span className="text-glass-text font-semibold">
                    {row.value} · {row.pct}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-glass-3 overflow-hidden">
                  <div className={`h-full rounded-full ${row.color}`} style={{ width: `${Math.max(row.pct, 2)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[0.95rem] font-semibold text-glass-text">Active Campaigns</h2>
            <Link to="/agency/campaigns" className="inline-flex items-center gap-1 text-xs font-semibold text-accentText">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {activeJobs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-glass-text-2 mb-4 text-sm">No active campaigns yet.</p>
              <button type="button" className="btn-accent px-4 py-2 text-sm font-semibold" onClick={() => navigate('/agency/campaigns/new')}>
                Create Campaign
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {activeJobs.slice(0, 5).map((job) => {
                const denom = Math.max(job.broadcastedCount || 1, 1);
                const progress = Math.min(100, Math.round(((job.approvedTalentCount || 0) / denom) * 100));
                return (
                  <button
                    key={job.id}
                    type="button"
                    onClick={() => navigate(`/agency/campaigns/${jobUrlSegment(job)}`)}
                    className="w-full text-left p-4 rounded-[14px] border border-glass bg-glass-2 hover:bg-glass-3 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-glass-text truncate">{job.title}</span>
                          <span className={statusPill(job.status)}>{job.status}</span>
                        </div>
                        <p className="text-xs text-glass-text-3 mt-1">
                          {job.brandOrg?.name || '—'}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-[rgb(var(--pos))] shrink-0">
                        {job.approvedTalentCount || 0} approved
                      </span>
                    </div>
                    <div className="flex gap-3 mt-3 text-xs font-medium">
                      <span className="text-[rgb(var(--info))]">{job.broadcastedCount || 0} sent</span>
                      <span className="text-accentText">{job.acceptedCount || 0} accepted</span>
                      <span className="text-[rgb(var(--warn))]">{job.shortlistedCount || 0} shortlist</span>
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-glass-3 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${progress}%`,
                          background:
                            'linear-gradient(90deg, rgb(var(--accent-rgb)), rgb(var(--accent2-rgb)))',
                        }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-4">
          <OnboardingChecklist title="Agency Onboarding" items={onboardingItems} />
          <div className="glass-card p-5">
            <h2 className="text-[0.95rem] font-semibold text-glass-text mb-3">Quick Actions</h2>
            <div className="space-y-2">
              <button type="button" className="btn-accent w-full justify-center py-3 text-sm font-semibold inline-flex items-center gap-2" onClick={() => navigate('/agency/campaigns/new')}>
                <Briefcase className="w-4 h-4" /> New Campaign
              </button>
              <button type="button" className="btn-glass w-full justify-center py-3 text-sm font-semibold inline-flex items-center gap-2" onClick={() => navigate('/agency/kols')}>
                <Users className="w-4 h-4" /> KOL Database
              </button>
              <button type="button" className="btn-glass w-full justify-center py-3 text-sm font-semibold inline-flex items-center gap-2" onClick={() => navigate('/agency/brands')}>
                <Target className="w-4 h-4" /> Manage Brands
              </button>
            </div>
          </div>
        </div>

        <div className="glass-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[0.95rem] font-semibold text-glass-text">Recent KOLs</h2>
            <Link to="/agency/kols" className="inline-flex items-center gap-1 text-xs font-semibold text-accentText">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {kols.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-glass-text-2 mb-4 text-sm">No KOLs yet. Add your first influencer.</p>
              <Link to="/agency/kols" className="btn-accent inline-flex px-4 py-2 text-sm font-semibold">
                Add KOL
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {kols.slice(0, 6).map((kol) => (
                <button
                  key={kol.id}
                  type="button"
                  onClick={() => navigate(`/agency/kols/${kolUrlSegment(kol)}`)}
                  className="flex items-center gap-3 p-3 rounded-[14px] border border-glass bg-glass-2 hover:bg-glass-3 hover:border-accent2/40 transition-all text-left"
                >
                  {kol.profileImageUrl ? (
                    <img src={resolveMediaUrl(kol.profileImageUrl)} alt={kol.name} className="w-11 h-11 rounded-full object-cover" />
                  ) : (
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold text-white"
                      style={{
                        background:
                          'linear-gradient(135deg, rgb(var(--accent-rgb) / 0.85), rgb(var(--accent2-rgb) / 0.65))',
                      }}
                    >
                      {kol.name[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-glass-text truncate text-sm">{kol.name}</div>
                    <div className="text-xs text-glass-text-3 truncate">{kol.email || 'No email'}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
